import React, { useState, useEffect } from "react";
import { Search, Book, Clock, ArrowDownCircle, ArrowUpCircle, Filter, Calendar, AlertCircle } from "lucide-react";
import "../../css/IssueReturn.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../../components/Loader";

function IssueReturn() {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDetails, setStudentDetails] = useState(null);
  const [showStudentDetails, setShowStudentDetails] = useState(false);

  useEffect(() => {
    fetchTransactions();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [transactions, searchTerm, statusFilter, dateFilter]);

  const fetchTransactions = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized! Please login again.");
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetch("https://schoolpro-api.uniums.com/api/books/transactions", {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch transactions");
      }

      const data = await response.json();
      setTransactions(data);
      setFilteredTransactions(data);
    } catch (error) {
      console.error("Fetch Error:", error);
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...transactions];
    if (searchTerm) {
      result = result.filter(
        (item) =>
          item.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.bookTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (item.remarks && item.remarks.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (item.isbn && item.isbn.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (statusFilter !== "all") {
      result = result.filter((item) => item.status === statusFilter);
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (dateFilter === "today") {
      result = result.filter((item) => {
        const issueDate = new Date(item.issueDate);
        issueDate.setHours(0, 0, 0, 0);
        return issueDate.getTime() === today.getTime();
      });
    } else if (dateFilter === "week") {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      weekAgo.setHours(0, 0, 0, 0);
      
      result = result.filter((item) => {
        const issueDate = new Date(item.issueDate);
        return issueDate >= weekAgo;
      });
    } else if (dateFilter === "month") {
      const monthAgo = new Date();
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      monthAgo.setHours(0, 0, 0, 0);
      
      result = result.filter((item) => {
        const issueDate = new Date(item.issueDate);
        return issueDate >= monthAgo;
      });
    }

    setFilteredTransactions(result);
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const calculateStatus = (transaction) => {
    if (transaction.status === "returned") {
      return { status: "returned", className: "issuereturn-status-returned" };
    } else if (transaction.status === "lost") {
      return { status: "lost", className: "issuereturn-status-lost" };
    } else {
      const today = new Date();
      const dueDate = new Date(transaction.dueDate);
      
      if (dueDate < today) {
        return { status: "overdue", className: "issuereturn-status-overdue" };
      } else {
        return { status: "issued", className: "issuereturn-status-issued" };
      }
    }
  };

  const calculateDaysRemaining = (dueDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  const viewStudentDetails = async (studentId) => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized! Please login again.");
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetch(`https://schoolpro-api.uniums.com/api/books/student/${studentId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch student details");
      }

      const data = await response.json();
      setStudentDetails(data);
      setSelectedStudent(studentId);
      setShowStudentDetails(true);
    } catch (error) {
      console.error("Fetch Error:", error);
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const closeStudentDetails = () => {
    setShowStudentDetails(false);
    setSelectedStudent(null);
    setStudentDetails(null);
  };

  return (
    <div className="issuereturn-container">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="issuereturn-header">
        <h2>Book Transaction History</h2>
        <p>View and manage book issues and returns</p>
      </div>

      <div className="issuereturn-filters">
        <div className="issuereturn-search">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by student, book title or ISBN..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="issuereturn-filter-group">
          <div className="issuereturn-filter">
            <label><Filter size={16} /> Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="issued">Issued</option>
              <option value="returned">Returned</option>
              <option value="overdue">Overdue</option>
              <option value="lost">Lost</option>
            </select>
          </div>

          <div className="issuereturn-filter">
            <label><Calendar size={16} /> Period:</label>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
            </select>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="issuereturn-loading">
          <Loader />
          <p>Loading transaction history...</p>
        </div>
      ) : (
        <>
          <div className="issuereturn-stats">
            <div className="issuereturn-stat-card">
              <div className="issuereturn-stat-icon issuereturn-icon-issued">
                <ArrowDownCircle size={24} />
              </div>
              <div className="issuereturn-stat-content">
                <h3>{transactions.filter(t => t.status === "issued").length}</h3>
                <p>Books Issued</p>
              </div>
            </div>
            <div className="issuereturn-stat-card">
              <div className="issuereturn-stat-icon issuereturn-icon-returned">
                <ArrowUpCircle size={24} />
              </div>
              <div className="issuereturn-stat-content">
                <h3>{transactions.filter(t => t.status === "returned").length}</h3>
                <p>Books Returned</p>
              </div>
            </div>
            <div className="issuereturn-stat-card">
              <div className="issuereturn-stat-icon issuereturn-icon-overdue">
                <Clock size={24} />
              </div>
              <div className="issuereturn-stat-content">
                <h3>{transactions.filter(t => t.status === "overdue").length}</h3>
                <p>Overdue Books</p>
              </div>
            </div>
            <div className="issuereturn-stat-card">
              <div className="issuereturn-stat-icon issuereturn-icon-fine">
                <AlertCircle size={24} />
              </div>
              <div className="issuereturn-stat-content">
                <h3>₹{transactions.reduce((total, t) => total + (t.fineAmount || 0), 0)}</h3>
                <p>Total Fines</p>
              </div>
            </div>
          </div>

          {filteredTransactions.length === 0 ? (
            <div className="issuereturn-no-data">
              <Book size={48} />
              <h3>No Transactions Found</h3>
              <p>There are no transactions matching your search or filter criteria.</p>
            </div>
          ) : (
            <div className="issuereturn-table-container">
              <table className="issuereturn-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Book Title</th>
                    <th>Issue Date</th>
                    <th>Due Date</th>
                    <th>Return Date</th>
                    <th>Status</th>
                    <th>Fine</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.map((transaction) => {
                    const { status, className } = calculateStatus(transaction);
                    const daysRemaining = transaction.status === "issued" ? calculateDaysRemaining(transaction.dueDate) : null;
                    
                    return (
                      <tr key={transaction._id} className={className}>
                        <td className="issuereturn-student-name" onClick={() => viewStudentDetails(transaction.studentId)}>
                          {transaction.studentName}
                        </td>
                        <td className="issuereturn-book-title">{transaction.bookTitle}</td>
                        <td>{formatDate(transaction.issueDate)}</td>
                        <td>{formatDate(transaction.dueDate)}</td>
                        <td>{transaction.returnDate ? formatDate(transaction.returnDate) : "-"}</td>
                        <td>
                          <span className={`issuereturn-status ${className}`}>
                            {status}
                            {status === "issued" && daysRemaining !== null && (
                              <span className="issuereturn-days-remaining">
                                {daysRemaining <= 0 
                                  ? " (Due today)" 
                                  : ` (${daysRemaining} day${daysRemaining === 1 ? '' : 's'} left)`}
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="issuereturn-fine">
                          {transaction.fineAmount > 0 ? 
                            <span className="issuereturn-fine-amount">${transaction.fineAmount}</span> : 
                            "-"}
                        </td>
                        <td>
                          <button 
                            className="issuereturn-action-btn"
                            onClick={() => viewStudentDetails(transaction.studentId)}
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Student Details Modal */}
      {showStudentDetails && (
        <div className="issuereturn-modal-overlay">
          <div className="issuereturn-modal">
            <div className="issuereturn-modal-header">
              <h3>Student Book History</h3>
              <button className="issuereturn-modal-close" onClick={closeStudentDetails}>×</button>
            </div>
            <div className="issuereturn-modal-body">
              {studentDetails && (
                <>
                  <div className="issuereturn-student-info">
                    <h4>{studentDetails.student.firstName} {studentDetails.student.lastName}</h4>
                    <p>Roll Number: {studentDetails.student.rollNumber || "N/A"}</p>
                    <p>Currently has {studentDetails.issuedBooks.length} book(s) checked out</p>
                  </div>
                  
                  <div className="issuereturn-student-books">
                    <h5>Current Books</h5>
                    {studentDetails.issuedBooks.length > 0 ? (
                      <table className="issuereturn-modal-table">
                        <thead>
                          <tr>
                            <th>Book Title</th>
                            <th>Issue Date</th>
                            <th>Due Date</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {studentDetails.issuedBooks.map((book) => {
                            const daysRemaining = calculateDaysRemaining(book.dueDate);
                            const isOverdue = daysRemaining < 0;
                            
                            return (
                              <tr key={book.transactionId}>
                                <td>{book.title}</td>
                                <td>{formatDate(book.issueDate)}</td>
                                <td>{formatDate(book.dueDate)}</td>
                                <td>
                                  <span className={`issuereturn-status ${isOverdue ? "issuereturn-status-overdue" : "issuereturn-status-issued"}`}>
                                    {isOverdue ? "Overdue" : "Issued"}
                                    <span className="issuereturn-days-remaining">
                                      {daysRemaining === 0 
                                        ? " (Due today)" 
                                        : isOverdue
                                          ? ` (${Math.abs(daysRemaining)} day${Math.abs(daysRemaining) === 1 ? '' : 's'} overdue)`
                                          : ` (${daysRemaining} day${daysRemaining === 1 ? '' : 's'} left)`}
                                    </span>
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    ) : (
                      <p className="issuereturn-no-books">No books currently checked out.</p>
                    )}
                  </div>
                  
                  <div className="issuereturn-student-history">
                    <h5>Book History</h5>
                    {studentDetails.history && studentDetails.history.length > 0 ? (
                      <table className="issuereturn-modal-table">
                        <thead>
                          <tr>
                            <th>Book Title</th>
                            <th>Issue Date</th>
                            <th>Return Date</th>
                            <th>Fine</th>
                          </tr>
                        </thead>
                        <tbody>
                          {studentDetails.history.map((item) => (
                            <tr key={item.transactionId}>
                              <td>{item.title}</td>
                              <td>{formatDate(item.issueDate)}</td>
                              <td>{formatDate(item.returnDate)}</td>
                              <td>{item.fineAmount > 0 ? `$${item.fineAmount}` : "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="issuereturn-no-books">No previous book history.</p>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="issuereturn-modal-footer">
              <button onClick={closeStudentDetails} className="issuereturn-modal-btn">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default IssueReturn;